/**
 * `/effectiveness` — Task PW2 (pillar web), consuming the Task P3 effectiveness
 * API. `web/shapes/effectiveness.md` is the agreed shape, written before this file.
 *
 * ══════════════════════════════════════════════════════════════════════════════
 *  GOALS FIRST. THE SCORE IS SECONDARY CONTEXT. THE TEAM IS A DISTRIBUTION.
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * The 2026 deep-research is explicit that activity/online-time scores are not a
 * valid productivity measure and are harmful when tied to a target — see
 * `src/modules/effectiveness/effectiveness.ts`'s own header. This screen is built
 * against that finding, on purpose, in three places:
 *
 *  - Every row renders goals **before** productivity, which renders **before**
 *    attendance — `GoalsCell`, then `ProductivityCell`, then `AttendanceCell`, in
 *    that literal component order, never re-sorted.
 *  - `ProductivityCell` never renders a bare number: `is_secondary_context` is
 *    `true` on the wire, and the confidence badge and the low-confidence note
 *    (reusing `myactivity/ScorePanel.tsx`'s own presentation) travel with the
 *    score wherever it appears.
 *  - `TeamDistribution` reads `EffectivenessTeamResponse`, which carries no field
 *    keyed by employee at all — there is no ranking to accidentally render,
 *    because the server never sent one.
 *
 * `insufficientData` renders **"chưa đủ dữ liệu"** for every `null` figure on this
 * screen — never a fabricated `0` (`PRODUCT.md` §4). The two independent reads
 * (list, team) each get their own `Skeleton`/`ApiErrorNote`/`EmptyState`, for the
 * same reason `PresenceScreen.tsx`'s own header gives: one slow region must not
 * hide a fast one behind a page-level spinner.
 */

import type { ReactNode } from 'react';

import { useI18n } from '../i18n';
import { useSession } from '../auth/SessionProvider';
import {
  effectivenessPath,
  effectivenessTeamPath,
  type EffectivenessAttendanceStats,
  type EffectivenessExplanationFlag,
  type EffectivenessGoalStats,
  type EffectivenessListResponse,
  type EffectivenessProductivityView,
  type EffectivenessQuartileSummary,
  type EffectivenessRow,
  type EffectivenessTeamResponse,
} from '../api/effectiveness';
import { useApiQuery } from '../api/useApiQuery';
import { ApiErrorNote } from '../states/ApiErrorNote';
import { Badge } from '../states/Badge';
import { EmptyState } from '../states/EmptyState';
import { LastSync } from '../states/LastSync';
import { Skeleton } from '../states/Skeleton';
import { StatusNote } from '../states/StatusNote';
import { ScreenHeading } from '../shell/ScreenHeading';

function formatPct1(value: number): string {
  return `${value.toFixed(1)}%`;
}

function formatFractionPct(value: number): string {
  return `${String(Math.round(value * 100))}%`;
}

function hasFlag(
  flags: readonly EffectivenessExplanationFlag[],
  flag: EffectivenessExplanationFlag,
): boolean {
  return flags.includes(flag);
}

/** Goals — the PRIMARY signal, rendered first on every row. */
function GoalsCell({
  goals,
  testId,
}: {
  readonly goals: EffectivenessGoalStats;
  readonly testId: string;
}): ReactNode {
  const { t } = useI18n();
  if (goals.total === 0) {
    return (
      <p className="wp-muted" data-testid={`${testId}-insufficient`}>
        {t('effectiveness.insufficientData')}
      </p>
    );
  }
  return (
    <>
      <p data-testid={`${testId}-counts`}>
        {t('effectiveness.goals.counts', { achieved: goals.achieved, total: goals.total })}
      </p>
      <p data-testid={`${testId}-pct`}>
        {goals.achievement_pct === null
          ? t('effectiveness.insufficientData')
          : t('effectiveness.goals.achievementPct', { value: formatPct1(goals.achievement_pct) })}
      </p>
    </>
  );
}

/**
 * Productivity — SECONDARY CONTEXT, never bare. Reuses `myactivity/ScorePanel.tsx`'s
 * own confidence-badge and low-confidence-note presentation, so the identical score
 * reads identically on both screens.
 */
function ProductivityCell({
  productivity,
  flags,
  testId,
}: {
  readonly productivity: EffectivenessProductivityView;
  readonly flags: readonly EffectivenessExplanationFlag[];
  readonly testId: string;
}): ReactNode {
  const { t } = useI18n();
  const lowConfidence = hasFlag(flags, 'low_confidence_score');

  if (productivity.value === null) {
    return (
      <p className="wp-muted" data-testid={`${testId}-insufficient`}>
        {t('effectiveness.insufficientData')}
      </p>
    );
  }

  return (
    <>
      <p data-testid={`${testId}-value`}>
        <span className="wp-visually-hidden">{t('effectiveness.productivity.value')}: </span>
        {productivity.value.toFixed(1)}
      </p>
      <p className="wp-flags">
        <Badge
          severity={lowConfidence ? 'medium' : 'low'}
          icon={lowConfidence ? 'circle-alert' : 'circle-check'}
          label={t('effectiveness.productivity.confidence', {
            value: formatFractionPct(productivity.confidence),
          })}
          title={t('effectiveness.productivity.confidence.help')}
          testId={`${testId}-confidence`}
        />
      </p>
      {lowConfidence ? (
        <StatusNote
          severity="medium"
          title={t('effectiveness.productivity.lowConfidence.title')}
          testId={`${testId}-low-confidence`}
        >
          <p>{t('effectiveness.productivity.lowConfidence.body')}</p>
        </StatusNote>
      ) : null}
    </>
  );
}

/** Attendance — the third signal, rendered last. */
function AttendanceCell({
  attendance,
  testId,
}: {
  readonly attendance: EffectivenessAttendanceStats;
  readonly testId: string;
}): ReactNode {
  const { t } = useI18n();
  return (
    <>
      <p data-testid={`${testId}-days`}>
        {t('effectiveness.attendance.days', {
          present: attendance.present_days,
          expected: attendance.expected_days,
        })}
      </p>
      <p data-testid={`${testId}-ontime`}>
        {attendance.on_time_rate === null
          ? t('effectiveness.insufficientData')
          : t('effectiveness.attendance.onTimeRate', {
              value: formatPct1(attendance.on_time_rate),
            })}
      </p>
    </>
  );
}

function EmployeeRow({ row }: { readonly row: EffectivenessRow }): ReactNode {
  const { t } = useI18n();
  const testId = `pw2-row-${row.employee_id}`;
  return (
    <tr data-testid={testId}>
      <th scope="row">
        <div>{row.full_name ?? row.employee_id}</div>
        <div className="wp-small wp-muted">
          {row.department_id === null
            ? t('effectiveness.noDepartment')
            : t('effectiveness.department', { id: row.department_id })}
        </div>
      </th>
      {/* Goals FIRST. */}
      <td data-testid={`${testId}-goals`}>
        <GoalsCell goals={row.goals} testId={`${testId}-goals`} />
      </td>
      {/* Productivity SECOND, secondary context. */}
      <td data-testid={`${testId}-productivity`}>
        <ProductivityCell
          productivity={row.productivity}
          flags={row.explanation_flags}
          testId={`${testId}-productivity`}
        />
      </td>
      {/* Attendance THIRD. */}
      <td data-testid={`${testId}-attendance`}>
        <AttendanceCell attendance={row.attendance} testId={`${testId}-attendance`} />
      </td>
    </tr>
  );
}

/**
 * The spread, drawn as a spread.
 *
 * Five numbers describing where a team sits are a **distribution**, and a row of
 * five cells makes the reader rebuild it in their head. So the same five numbers are
 * also drawn: the tails as a hairline, the middle half as an outlined box, the median
 * as one dark tick — with min, median and max labelled where they sit (`DESIGN.md`
 * §4 rule 3: a direct label, never a legend).
 *
 * There is no person in this figure and there cannot be one: `EffectivenessTeam`
 * carries no field keyed by employee, so no arrangement of it can become a ranking.
 *
 * The strip is `aria-hidden` because the table under it is the same five numbers in
 * a form a screen reader can read out, which is the only reason a decorative
 * duplicate of real data is allowed to exist at all.
 */
function QuartileStrip({ summary }: { readonly summary: EffectivenessQuartileSummary }): ReactNode {
  const span = summary.max - summary.min;
  // A team whose whole spread is one value has no width to draw. Everything lands
  // on the middle rather than on the left edge, where it would read as "lowest".
  const at = (value: number): string =>
    span <= 0 ? '50%' : `${String(((value - summary.min) / span) * 100)}%`;

  return (
    <div className="wp-b-dist" aria-hidden="true">
      <div className="wp-b-dist__track">
        <span
          className="wp-b-dist__whisker"
          style={{ left: at(summary.min), right: `calc(100% - ${at(summary.max)})` }}
        />
        <span className="wp-b-dist__cap" style={{ left: at(summary.min) }} />
        <span className="wp-b-dist__cap" style={{ left: at(summary.max) }} />
        <span
          className="wp-b-dist__box"
          style={{ left: at(summary.p25), right: `calc(100% - ${at(summary.p75)})` }}
        />
        <span className="wp-b-dist__median" style={{ left: at(summary.median) }} />
      </div>
      <div className="wp-b-dist__scale">
        <span className="wp-b-dist__tick wp-b-dist__tick--start">{summary.min}</span>
        <span
          className="wp-b-dist__tick wp-b-dist__tick--median"
          style={{ left: at(summary.median) }}
        >
          {summary.median}
        </span>
        <span className="wp-b-dist__tick wp-b-dist__tick--end" style={{ left: '100%' }}>
          {summary.max}
        </span>
      </div>
    </div>
  );
}

/** One quartile summary, or the insufficient-data note when the scope has none. */
function QuartileFigure({
  summary,
  testId,
  caption,
}: {
  readonly summary: EffectivenessQuartileSummary | null;
  readonly testId: string;
  readonly caption: string;
}): ReactNode {
  const { t } = useI18n();
  if (summary === null) {
    return (
      <div data-testid={`${testId}-insufficient`}>
        <p className="wp-b-dist__caption">{caption}</p>
        <p className="wp-muted">{t('effectiveness.insufficientData')}</p>
      </div>
    );
  }
  return (
    <div>
      <p className="wp-b-dist__caption">{caption}</p>
      <QuartileStrip summary={summary} />
      <div className="wp-table-scroll">
        <table className="wp-table" data-testid={testId}>
          <caption className="wp-visually-hidden">{caption}</caption>
          <thead>
            <tr>
              <th scope="col" className="wp-num">
                {t('effectiveness.team.col.min')}
              </th>
              <th scope="col" className="wp-num">
                {t('effectiveness.team.col.p25')}
              </th>
              <th scope="col" className="wp-num">
                {t('effectiveness.team.col.median')}
              </th>
              <th scope="col" className="wp-num">
                {t('effectiveness.team.col.p75')}
              </th>
              <th scope="col" className="wp-num">
                {t('effectiveness.team.col.max')}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="wp-num" data-testid={`${testId}-min`}>
                {summary.min}
              </td>
              <td className="wp-num" data-testid={`${testId}-p25`}>
                {summary.p25}
              </td>
              <td className="wp-num" data-testid={`${testId}-median`}>
                {summary.median}
              </td>
              <td className="wp-num" data-testid={`${testId}-p75`}>
                {summary.p75}
              </td>
              <td className="wp-num" data-testid={`${testId}-max`}>
                {summary.max}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function EffectivenessScreen(): ReactNode {
  const { t } = useI18n();
  const { user } = useSession();
  const timezone = user?.timezone ?? 'UTC';

  const listPath = effectivenessPath();
  const teamPath = effectivenessTeamPath();

  const list = useApiQuery<EffectivenessListResponse>(listPath);
  const team = useApiQuery<EffectivenessTeamResponse>(teamPath);

  const rows = list.data?.data ?? [];
  const framing = list.data?.framing ?? team.data?.framing ?? null;

  return (
    <div className="wp-stack wp-b">
      <div className="wp-screen-head">
        <ScreenHeading>{t('nav.effectiveness')}</ScreenHeading>
        {/* Both stamps, both `null`, both printed rather than hidden. Neither
            `EffectivenessListResponse` nor `EffectivenessTeamResponse` carries a
            computed-at, and this screen reads no device heartbeat, so there is
            nothing here to date — which is itself worth printing on a screen whose
            figures are about people. */}
        <LastSync agentSyncAt={null} achauSyncAt={null} timezone={timezone} />
      </div>

      <p className="wp-small wp-muted wp-b-lede" data-testid="pw2-intro">
        {t('effectiveness.intro')}
      </p>

      {/* ── The not-a-quota framing, carried from the server's own response ──── */}
      <StatusNote severity="info" title={t('effectiveness.framing.title')} testId="pw2-framing">
        <p>{t('effectiveness.framing.body')}</p>
        {framing === null ? null : <p data-testid="pw2-framing-purpose">{framing.purpose}</p>}
      </StatusNote>

      {/* ── Per-employee rows: goals first, productivity second, attendance third */}
      <section className="wp-section" aria-labelledby="pw2-list-heading">
        <h2 className="wp-section__title" id="pw2-list-heading">
          {t('effectiveness.list.heading')}
        </h2>

        {list.status === 'loading' ? (
          <Skeleton
            rows={4}
            variant="block"
            label={t('effectiveness.list.loading')}
            testId="pw2-list-skeleton"
          />
        ) : null}

        {list.error !== null ? (
          <ApiErrorNote
            error={list.error}
            onRetry={list.reload}
            subject={t('effectiveness.list.subject')}
            testId="pw2-list-error"
          />
        ) : null}

        {list.data !== null && rows.length === 0 ? (
          <EmptyState
            title={t('effectiveness.list.empty.title')}
            description={t('effectiveness.list.empty.body')}
            testId="pw2-list-empty"
          />
        ) : null}

        {list.data !== null && rows.length > 0 ? (
          <div className="wp-table-scroll">
            <table className="wp-table wp-b-people" data-testid="pw2-list">
              <thead>
                <tr>
                  <th scope="col">{t('effectiveness.list.col.employee')}</th>
                  <th scope="col">{t('effectiveness.list.col.goals')}</th>
                  <th scope="col">{t('effectiveness.list.col.productivity')}</th>
                  <th scope="col">{t('effectiveness.list.col.attendance')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <EmployeeRow key={row.employee_id} row={row} />
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>

      {/* ── Team: a DISTRIBUTION, never a ranked list of people ─────────────── */}
      <section className="wp-section" aria-labelledby="pw2-team-heading" data-testid="pw2-team">
        <h2 className="wp-section__title" id="pw2-team-heading">
          {t('effectiveness.team.heading')}
        </h2>
        <p className="wp-small wp-muted" data-testid="pw2-team-notRanking">
          {t('effectiveness.team.notRanking')}
        </p>

        {team.status === 'loading' ? (
          <Skeleton
            rows={4}
            variant="block"
            label={t('effectiveness.team.loading')}
            testId="pw2-team-skeleton"
          />
        ) : null}

        {team.error !== null ? (
          <ApiErrorNote
            error={team.error}
            onRetry={team.reload}
            subject={t('effectiveness.team.subject')}
            testId="pw2-team-error"
          />
        ) : null}

        {team.data === null ? null : (
          <>
            <p data-testid="pw2-team-memberCount">
              {t('effectiveness.team.memberCount', { count: team.data.member_count })}
            </p>
            <div className="wp-b-dists" data-testid="pw2-team-quartiles">
              <QuartileFigure
                summary={team.data.goal_achievement}
                testId="pw2-team-goal-quartiles"
                caption={t('effectiveness.team.goalAchievement')}
              />
              <QuartileFigure
                summary={team.data.productivity_score}
                testId="pw2-team-score-quartiles"
                caption={t('effectiveness.team.productivityScore')}
              />
            </div>
            <ul className="wp-b-counts" data-testid="pw2-team-counts">
              <li data-testid="pw2-team-lowConfidenceCount">
                {t('effectiveness.team.lowConfidenceCount', {
                  count: team.data.low_confidence_count,
                })}
              </li>
              <li data-testid="pw2-team-noGoalDataCount">
                {t('effectiveness.team.noGoalDataCount', { count: team.data.no_goal_data_count })}
              </li>
              <li data-testid="pw2-team-noProductivityScoreCount">
                {t('effectiveness.team.noProductivityScoreCount', {
                  count: team.data.no_productivity_score_count,
                })}
              </li>
            </ul>
          </>
        )}
      </section>
    </div>
  );
}
